import { useMemo } from 'react';
import {
  addDays,
  addMonths,
  addYears,
  differenceInWeeks,
  eachMonthOfInterval,
  eachWeekOfInterval,
  isSameMonth,
} from 'date-fns';
import { format } from 'date-fns/fp';

import { Tooltip } from '@/components/ui/tooltip';
import { useNow } from '@/hooks/useNow';
import { cls } from '@/utils/cls';
import { formatDate } from '@/utils/format/date';
import { label } from '@/utils/format/label';

import { inverterValues } from './inverter-values';

export const weekDays = [0, 1, 2, 3, 4, 5, 6] as const;

function getColorClass(nb: number): string {
  if (nb >= 31) return 'bg-primary-950';
  if (nb >= 30) return 'bg-primary-800';
  if (nb >= 29) return 'bg-primary-600';
  if (nb >= 28) return 'bg-primary-400';
  if (nb >= 27) return 'bg-primary-300';
  return 'bg-primary-100';
}

const dateKey = format('yyyy-MM-dd');
const monthKey = format('yyyy-MM');
const formatMonth = format('MMMM');

const valuesPerDate = Object.fromEntries(
  inverterValues.map(({ date, nb }) => [dateKey(date), nb]),
);

export function WhiteLabelInverterOnlines() {
  const now = useNow();

  const { weeks, months } = useMemo(
    function () {
      const end = now;
      const start = addYears(addMonths(now, 4), -1);
      const weeks = eachWeekOfInterval({ start, end });
      const months = eachMonthOfInterval({ start, end });

      return { weeks, months };
    },
    [now],
  );

  return (
    <div className="flex flex-col gap-4 overflow-hidden rounded-lg bg-white p-4 pt-8 shadow">
      <h2 className="text-center text-xl font-semibold text-gray-900">
        Online Inverters Activity
      </h2>
      <div
        className="grid min-w-[750px] gap-0.5"
        style={{
          gridTemplateColumns: `repeat(${weeks.length + 1}, minmax(0, 1fr))`,
        }}
      >
        <div />
        {months.map((month) => {
          const firstWeek = weeks.find((week) =>
            isSameMonth(addDays(week, 6), month),
          );

          if (!firstWeek) {
            return null;
          }

          const lastWeek = weeks.findLast((week) =>
            isSameMonth(addDays(week, 6), month),
          );

          if (!lastWeek) {
            return null;
          }

          const size = 1 + differenceInWeeks(lastWeek, firstWeek);

          return (
            <div
              key={monthKey(month)}
              className="items-center justify-center truncate pl-1 text-left text-xs"
              style={{ gridColumn: `span ${size} / span ${size}` }}
            >
              {formatMonth(month)}
            </div>
          );
        })}

        <div className="grid grid-rows-7 items-center justify-center overflow-hidden text-xs">
          <div className="aspect-square w-full text-right">S</div>
          <div className="aspect-square w-full text-right">M</div>
          <div className="aspect-square w-full text-right">T</div>
          <div className="aspect-square w-full text-right">W</div>
          <div className="aspect-square w-full text-right">T</div>
          <div className="aspect-square w-full text-right">F</div>
          <div className="aspect-square w-full text-right">S</div>
        </div>
        {weeks.map((week) => (
          <div
            key={dateKey(week)}
            className="square grid gap-0.5"
            style={{
              gridTemplateRows: `repeat(${weekDays.length}, minmax(0, 1fr))`,
            }}
          >
            {weekDays.map((dayOffset) => {
              const date = addDays(week, dayOffset);
              const key = dateKey(date);
              const value = valuesPerDate[key];

              return (
                <Tooltip
                  key={key}
                  content={
                    <p className="text-center">
                      {value ? (
                        <>
                          <strong>
                            {value} {label(['inverter', 'inverters'], value)}{' '}
                            online
                          </strong>
                          <br />
                        </>
                      ) : null}
                      {formatDate(date)}
                    </p>
                  }
                  clickToKeepOpen
                >
                  <div
                    key={key}
                    className={cls(
                      'aspect-square w-full overflow-hidden rounded',
                      value ? getColorClass(value) : 'bg-gray-200',
                      {
                        'cursor-pointer hover:ring-1 hover:ring-black': value,
                      },
                    )}
                    title={value ? `${formatDate(date)}: ` : formatDate(date)}
                  />
                </Tooltip>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex flex-col justify-center gap-2 self-end">
        <div className="mx-4 mt-4 flex items-center justify-end gap-2 text-xs text-gray-500">
          <span>28</span>
          <div className="flex gap-1">
            <div className="size-5 rounded bg-primary-100" />
            <div className="size-5 rounded bg-primary-300" />
            <div className="size-5 rounded bg-primary-400" />
            <div className="size-5 rounded bg-primary-600" />
            <div className="size-5 rounded bg-primary-800" />
            <div className="size-5 rounded bg-primary-950" />
          </div>
          <span>31</span>
        </div>
        <div className="text-center text-xs text-gray-500">Number online</div>
      </div>
    </div>
  );
}

/*





<div className="relative flex flex-col gap-4 overflow-hidden rounded-lg bg-white px-4 py-8 shadow">
        <div className="mb-2 flex gap-1 text-xs text-gray-500">
          {monthLabels.map((label, i) => (
            <span key={i} className="w-6 text-center">
              {i === 0 || label !== monthLabels[i - 1] ? label : ''}
            </span>
          ))}
        </div>
        <div className="flex">
          <div className="mr-2 flex h-36 flex-col justify-between text-xs text-gray-400">
            {['M', 'W', 'F', 'S'].map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>
          <div className="flex">
            {pwet.map((week, x) => (
              <div key={x} className="flex flex-col gap-1">
                {weekDays.map((weekDay, y) => {
                  const currentDate = addDays(week, weekDay);
                  const value = data[dateKey(currentDate)];
                  const nb = value?.nb;
                  return (
                    <div
                      key={y}
                      className={`h-6 w-6 rounded ${nb ? getColorClass(nb) : 'bg-gray-200'} cursor-pointer border border-white`}
                      title={
                        nb
                          ? `${formatDate(currentDate)}: ${nb} online`
                          : formatDate(currentDate)
                      }
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
          <span>28</span>
          <div className="flex gap-1">
            <div className="h-5 w-5 rounded bg-[var(--color-primary-100)]" />
            <div className="h-5 w-5 rounded bg-[var(--color-primary-300)]" />
            <div className="h-5 w-5 rounded bg-[var(--color-primary-400)]" />
            <div className="h-5 w-5 rounded bg-[var(--color-primary-600)]" />
            <div className="h-5 w-5 rounded bg-[var(--color-primary-800)]" />
            <div className="h-5 w-5 rounded bg-[var(--color-primary-950)]" />
          </div>
          <span>31</span>
        </div>
        <div className="mt-1 text-xs text-gray-600">Inverters online</div>
      </div>
			*/
